import React, { useCallback, useState } from 'react';
import Button from '@atlaskit/button/default/button';
import IconButton from '@atlaskit/button/icon/button';
import { cssMap } from '@atlaskit/css';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';
import { Modal } from '@atlassian/growth-pattern-library-modal/Modal';
import { defineMessage, FormattedMessage } from '@atlassian/jira-intl';
import { JiraModal as ModalDialog } from '@atlassian/jira-modal/src/ui/jira-modal.tsx';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	fireUIAnalytics,
	MODAL,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';

import onboardingIllustration from './assets-deeplinking-modal-illustration.svg';
import {
	persistAssetsDeeplinkingModalClosed,
	readAssetsDeeplinkingModalShouldShow,
} from './readAssetsDeeplinkingModalShouldShow.tsx';

const MODAL_WIDTH = 968;
const MODAL_HEIGHT = 499;

const assetsDeeplinkingModalHeading = defineMessage({
	id: 'servicedesk-insight-reports-v2.assets-deeplinking-modal.heading',
	defaultMessage: 'See the data behind your charts',
	description: 'Heading for the assets deeplinking modal',
});

const assetsDeeplinkingModalDescription1 = defineMessage({
	id: 'servicedesk-insight-reports-v2.assets-deeplinking-modal.description1',
	defaultMessage:
		'You can now click directly on data points in your charts to drill in and see the underlying records. For example, if you select a bar in the “ticket volume” chart, we’ll open a detailed view showing the actual tickets that make up that data point.',
	description: 'First paragraph of the description for the assets deeplinking modal',
});

const assetsDeeplinkingModalDescription2 = defineMessage({
	id: 'servicedesk-insight-reports-v2.assets-deeplinking-modal.description2',
	defaultMessage:
		'This lets you move from high-level trends to the raw sources behind them in just one click.',
	description: 'Second paragraph of the description for the assets deeplinking modal',
});

const assetsDeeplinkingModalDoneButton = defineMessage({
	id: 'servicedesk-insight-reports-v2.assets-deeplinking-modal.doneButton',
	defaultMessage: 'Done',
	description: 'Description for the done button in the assets deeplinking modal',
});

const assetsDeeplinkingModalCloseButton = defineMessage({
	id: 'servicedesk-insight-reports-v2.assets-deeplinking-modal.closeButton',
	defaultMessage: 'Close',
	description: 'Description for the close button in the assets deeplinking modal',
});

const ModalActions = ({ onDoneClicked }: { onDoneClicked: () => void }) => (
	<>
		<Button appearance="primary" onClick={onDoneClicked} autoFocus>
			<FormattedMessage {...assetsDeeplinkingModalDoneButton} />
		</Button>
	</>
);

const styles = cssMap({
	illustrationImage: {
		display: 'block',
		height: '100%',
		inset: token('space.0'),
		maxHeight: '100%',
		maxWidth: '100%',
		objectFit: 'cover',
		objectPosition: 'center center',
		position: 'absolute',
		width: '100%',
	},
	imageWrapper: {
		alignItems: 'center',
		backgroundColor: token('color.background.accent.yellow.subtler'),
		display: 'flex',
		height: '100%',
		justifyContent: 'center',
		overflow: 'hidden',
		position: 'relative',
		width: '100%',
	},
	closeButtonWrapper: {
		backgroundColor: token('color.background.accent.gray.subtlest'),
		borderColor: token('color.border.accent.gray'),
		borderRadius: token('radius.small'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		position: 'absolute',
		right: token('space.200'),
		top: token('space.200'),
	},
});

const CloseIcon = () => <CrossIcon label="Close" color={token('color.icon')} />;

const OnboardingImage = ({ onClose }: { onClose: () => void }) => (
	<Box xcss={styles.imageWrapper}>
		<Box xcss={styles.illustrationImage} as="img" src={onboardingIllustration} alt="" />
		<Box xcss={styles.closeButtonWrapper}>
			<IconButton
				appearance="subtle"
				icon={CloseIcon}
				onClick={onClose}
				label={<FormattedMessage {...assetsDeeplinkingModalCloseButton} />}
			/>
		</Box>
	</Box>
);

const AssetsDeeplinkingModal = (): React.JSX.Element | null => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isModalOpen, setIsModalOpen] = useState(() => readAssetsDeeplinkingModalShouldShow());

	const persistModalClosed = useCallback(() => {
		persistAssetsDeeplinkingModalClosed();
		setIsModalOpen(false);
	}, []);

	const handleDismiss = useCallback(async () => {
		const analyticsEvent = createAnalyticsEvent({});
		fireUIAnalytics(analyticsEvent, {
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'assetsDashboardsDeepLinkingModal',
			attributes: {
				buttonId: 'cross',
			},
		});
		persistModalClosed();
	}, [createAnalyticsEvent, persistModalClosed]);

	const onDoneClicked = useCallback(async () => {
		const analyticsEvent = createAnalyticsEvent({});
		fireUIAnalytics(analyticsEvent, {
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'assetsDashboardsDeepLinkingModal',
			attributes: {
				buttonId: 'doneButton',
			},
		});
		persistModalClosed();
	}, [createAnalyticsEvent, persistModalClosed]);

	if (!isModalOpen) {
		return null;
	}

	return (
		<ContextualAnalyticsData sourceName="assetsDashboardsDeepLinkingOnboarding" sourceType={MODAL}>
			<ModalDialog
				height={MODAL_HEIGHT}
				messageId="insight-reports-v2.modal-dialog.jsm-insight-reports-v2.assets-deeplinking-modal.assets-deeplinking-modal"
				messageType="transactional"
				onClose={handleDismiss}
				width={MODAL_WIDTH}
			>
				<FireScreenAnalytics />
				<Modal
					renderedInsideEntrypoint
					title={<FormattedMessage {...assetsDeeplinkingModalHeading} />}
					actions={<ModalActions onDoneClicked={onDoneClicked} />}
					sideContent={<OnboardingImage onClose={handleDismiss} />}
				>
					<>
						<Text as="p" size="medium">
							<FormattedMessage {...assetsDeeplinkingModalDescription1} />
						</Text>
						<Text as="p" size="medium">
							<FormattedMessage {...assetsDeeplinkingModalDescription2} />
						</Text>
					</>
				</Modal>
			</ModalDialog>
		</ContextualAnalyticsData>
	);
};

export default AssetsDeeplinkingModal;
